﻿import { text } from "./locales.js";
import type {
  BusinessActivity,
  CountryPack,
  CountryProfile,
  CountrySpecificCheckResult,
  CountryThresholdRule,
  PreparedInvoice
} from "./types.js";

const sgThresholdRules: CountryThresholdRule[] = [
  {
    id: "sg-gst-retrospective",
    title: text("GST retrospective threshold", "GST 追溯注册阈值", "Ambang retrospektif GST", "Ambang retrospektif GST"),
    businessActivities: ["general_services", "professional_services", "it_services", "advertising_services", "goods", "retail", "other"],
    threshold: 1_000_000,
    currency: "SGD",
    rate: 9,
    currentLogic: "calendar_year",
    sourceIds: ["SG_IRAS_GST_REGISTRATION"]
  },
  {
    id: "sg-gst-prospective",
    title: text("GST prospective threshold", "GST 预测注册阈值", "Ambang prospektif GST", "Ambang prospektif GST"),
    businessActivities: ["general_services", "professional_services", "it_services", "advertising_services", "goods", "retail", "other"],
    threshold: 1_000_000,
    currency: "SGD",
    rate: 9,
    currentLogic: "projected_next_12_months",
    sourceIds: ["SG_IRAS_GST_REGISTRATION"]
  }
];

const myThresholdRules: CountryThresholdRule[] = [
  {
    id: "my-einvoice-timeline",
    title: text("Malaysia e-Invoice rollout", "马来西亚 e-Invoice 实施门槛", "Pelaksanaan e-Invoice Malaysia", "Penerapan e-Invoice Malaysia"),
    businessActivities: [
      "general_services",
      "professional_services",
      "it_services",
      "advertising_services",
      "food_and_beverage",
      "logistics",
      "goods",
      "retail",
      "other"
    ],
    threshold: 1_000_000,
    currency: "MYR",
    currentLogic: "country_schedule",
    sourceIds: ["MY_HASIL_EINVOICE_TIMELINE", "MY_HASIL_EINVOICE_FAQ"]
  },
  {
    id: "my-service-tax-500k",
    title: text(
      "Malaysia service tax threshold (professional / IT / advertising)",
      "马来西亚服务税门槛（专业服务 / IT / 广告）",
      "Ambang cukai perkhidmatan Malaysia (profesional / IT / pengiklanan)",
      "Ambang pajak layanan Malaysia (profesional / TI / iklan)"
    ),
    businessActivities: ["professional_services", "it_services", "advertising_services", "general_services"],
    threshold: 500_000,
    currency: "MYR",
    rate: 8,
    currentLogic: "past_12_months",
    sourceIds: ["MY_MYSST_REGISTRATION_TABLE", "MY_MYSST_BACKGROUND", "MY_MYSST_IT_GUIDE"]
  },
  {
    id: "my-service-tax-fnb",
    title: text("Malaysia service tax threshold (F&B)", "马来西亚服务税门槛（餐饮）", "Ambang cukai perkhidmatan Malaysia (F&B)", "Ambang pajak layanan Malaysia (F&B)"),
    businessActivities: ["food_and_beverage"],
    threshold: 1_500_000,
    currency: "MYR",
    rate: 6,
    currentLogic: "past_12_months",
    sourceIds: ["MY_MYSST_REGISTRATION_TABLE", "MY_MYSST_BACKGROUND"]
  },
  {
    id: "my-service-tax-logistics",
    title: text("Malaysia service tax threshold (logistics)", "马来西亚服务税门槛（物流）", "Ambang cukai perkhidmatan Malaysia (logistik)", "Ambang pajak layanan Malaysia (logistik)"),
    businessActivities: ["logistics"],
    threshold: 500_000,
    currency: "MYR",
    rate: 6,
    currentLogic: "past_12_months",
    sourceIds: ["MY_MYSST_REGISTRATION_TABLE", "MY_MYSST_BACKGROUND"]
  }
];

const idThresholdRules: CountryThresholdRule[] = [
  {
    id: "id-pkp-threshold",
    title: text("Indonesia PKP threshold", "印尼 PKP 注册门槛", "Ambang PKP Indonesia", "Ambang PKP Indonesia"),
    businessActivities: ["general_services", "professional_services", "it_services", "advertising_services", "goods", "luxury_goods", "retail", "other"],
    threshold: 4_800_000_000,
    currency: "IDR",
    rate: 11,
    currentLogic: "past_12_months",
    sourceIds: ["ID_DJP_PPN_COLLECTION"]
  }
];

const thThresholdRules: CountryThresholdRule[] = [
  {
    id: "th-vat-threshold",
    title: text("Thailand VAT registration threshold", "泰国 VAT 注册门槛", "Ambang pendaftaran VAT Thailand", "Ambang pendaftaran VAT Thailand", "เกณฑ์การจดทะเบียน VAT ไทย"),
    businessActivities: [
      "general_services",
      "professional_services",
      "it_services",
      "advertising_services",
      "food_and_beverage",
      "logistics",
      "goods",
      "luxury_goods",
      "retail",
      "other"
    ],
    threshold: 1_800_000,
    currency: "THB",
    rate: 7,
    currentLogic: "past_12_months",
    sourceIds: ["TH_RD_VAT_OVERVIEW", "TH_RD_VAT_RATE_2026"]
  }
];

const includesTaxNote = (invoice: PreparedInvoice): boolean =>
  (invoice.notes ?? []).some((note) => note.toLowerCase().includes("includes gst") || note.includes("Price payable includes GST"));

const validateSingaporeRegistration = (value: string): boolean => {
  const normalized = value.trim().toUpperCase();
  return (
    /^\d{8}[A-Z]$/.test(normalized) ||
    /^\d{9}[A-Z]$/.test(normalized) ||
    /^[A-Z]\d{7}[A-Z]$/.test(normalized) ||
    /^\d{9,10}[A-Z]$/.test(normalized) ||
    /^[TS]\d{2}[A-Z]{2}\d{4}[A-Z]$/.test(normalized)
  );
};

const validateMalaysiaTin = (value: string): boolean => /^[A-Z]{0,2}\d{8,12}$/.test(value.trim().toUpperCase());
const validateIndonesiaTaxNumber = (value: string): boolean => /^\d{15,16}$/.test(value.replaceAll(/\D/g, ""));
const validateThailandTaxNumber = (value: string): boolean => /^\d{13}$/.test(value.replaceAll(/\D/g, ""));

const malaysiaRateByActivity = (activity: BusinessActivity | undefined): number => {
  switch (activity) {
    case "food_and_beverage":
    case "logistics":
      return 6;
    case "professional_services":
    case "it_services":
    case "advertising_services":
    case "general_services":
    case "other":
      return 8;
    default:
      return 8;
  }
};

const indonesiaRateByActivity = (activity: BusinessActivity | undefined): number => {
  if (activity === "luxury_goods") {
    return 12;
  }
  return 11;
};

const thailandInvoiceMode = (invoice: PreparedInvoice): string => {
  if (invoice.invoiceType?.trim()) {
    return invoice.invoiceType.trim();
  }
  if (invoice.eInvoiceRequested) {
    return "etax_invoice";
  }
  return invoice.taxRegistered ? "tax_invoice" : "commercial_invoice";
};

const sgProfile = (invoice: PreparedInvoice): CountryProfile => {
  const isSimplified =
    Boolean(invoice.taxRegistered) &&
    invoice.buyerType === "final_consumer" &&
    invoice.totals.grandTotal <= 1_000;

  if (!invoice.taxRegistered) {
    return {
      key: "profile_sg_non_registered",
      label: text("Singapore commercial invoice", "新加坡普通商业发票", "Invois komersial Singapura", "Invoice komersial Singapura"),
      requiredFields: ["invoiceNumber", "issueDate", "totals.currency", "seller.legalName", "seller.registrationNumber", "seller.address", "items"],
      requiredLineFields: ["description", "quantity", "unitPrice", "amountExcludingTax", "totalAmount"],
      allowedTaxRates: [0],
      rateNote: text("Non-registered businesses should not charge GST.", "未注册 GST 的企业不应计收 GST。", "Perniagaan tidak berdaftar tidak boleh mengenakan GST.", "Usaha yang belum terdaftar tidak boleh memungut GST."),
      sourceIds: ["SG_IRAS_INVOICING_CUSTOMERS", "SG_IRAS_GST_REGISTRATION"]
    };
  }

  if (isSimplified) {
    return {
      key: "profile_sg_simplified",
      label: text("Singapore simplified tax invoice", "新加坡简化税务发票", "Invois cukai ringkas Singapura", "Faktur pajak sederhana Singapura"),
      requiredFields: [
        "invoiceNumber",
        "issueDate",
        "totals.currency",
        "seller.legalName",
        "seller.registrationNumber",
        "seller.taxNumber",
        "seller.address"
      ],
      requiredLineFields: ["description", "totalAmount"],
      allowedTaxRates: [9],
      rateNote: text("Simplified tax invoices up to SGD 1,000 must still show GST-inclusive wording.", "不超过 1,000 新元的简化税务发票仍应包含“价格已含 GST”的说明。", "Invois cukai ringkas sehingga SGD 1,000 masih perlu menunjukkan kenyataan harga termasuk GST.", "Faktur pajak sederhana sampai SGD 1.000 tetap harus menampilkan pernyataan harga termasuk GST."),
      sourceIds: ["SG_IRAS_INVOICING_CUSTOMERS"]
    };
  }

  return {
    key: "profile_sg_standard",
    label: text("Singapore GST tax invoice", "新加坡 GST 税务发票", "Invois cukai GST Singapura", "Faktur pajak GST Singapura"),
    requiredFields: [
      "invoiceNumber",
      "issueDate",
      "totals.currency",
      "seller.legalName",
      "seller.registrationNumber",
      "seller.taxNumber",
      "seller.address",
      "buyer.legalName",
      "buyer.address"
    ],
    requiredLineFields: ["description", "quantity", "unitPrice", "amountExcludingTax", "taxRate", "taxAmount", "totalAmount"],
    allowedTaxRates: invoice.supplyNature === "domestic" ? [9] : [0, 9],
    rateNote: text(
      "Domestic standard-rated supplies are 9%; zero-rating is only available if the international-services rules are met.",
      "境内标准税率为 9%；只有满足国际服务条件时才可以适用零税率。",
      "Bekalan domestik standard dikenakan 9%; kadar sifar hanya dibenarkan jika syarat perkhidmatan antarabangsa dipenuhi.",
      "Penyerahan domestik standar dikenai 9%; tarif 0% hanya berlaku bila syarat jasa internasional terpenuhi."
    ),
    sourceIds: ["SG_IRAS_INVOICING_CUSTOMERS", "SG_IRAS_INTL_SERVICES"]
  };
};

const myProfile = (invoice: PreparedInvoice): CountryProfile => {
  const turnover = invoice.turnoverLast12Months ?? 0;
  const eInvoiceRequired = invoice.eInvoiceRequested || turnover >= 1_000_000;

  if (!invoice.taxRegistered && !eInvoiceRequired) {
    return {
      key: "profile_my_non_registered",
      label: text("Malaysia commercial invoice", "马来西亚普通商业发票", "Invois komersial Malaysia", "Invoice komersial Malaysia"),
      requiredFields: ["invoiceNumber", "issueDate", "totals.currency", "seller.legalName", "seller.registrationNumber", "seller.address"],
      requiredLineFields: ["description", "quantity", "unitPrice", "amountExcludingTax", "totalAmount"],
      allowedTaxRates: [0],
      rateNote: text("If you are not registered for SST, the invoice should not charge SST.", "如果尚未注册 SST，则发票不应计收 SST。", "Jika belum berdaftar SST, invois tidak boleh mengenakan SST.", "Jika belum terdaftar SST, invoice tidak boleh memungut SST."),
      sourceIds: ["MY_MYSST_TAX_INVOICES", "MY_HASIL_EINVOICE_TIMELINE"]
    };
  }

  if (eInvoiceRequired) {
    return {
      key: "profile_my_einvoice",
      label: text("Malaysia e-Invoice draft", "马来西亚 e-Invoice 草稿", "Draf e-Invois Malaysia", "Draf e-Invoice Malaysia"),
      requiredFields: [
        "invoiceNumber",
        "issueDate",
        "issueTime",
        "totals.currency",
        "seller.legalName",
        "seller.registrationNumber",
        "seller.taxNumber",
        "seller.address",
        "seller.phone",
        "buyer.legalName",
        "buyer.taxNumber",
        "buyer.address",
        "buyer.phone"
      ],
      requiredLineFields: ["description", "classificationCode", "quantity", "unitPrice", "amountExcludingTax", "taxRate", "taxAmount", "totalAmount"],
      allowedTaxRates: invoice.taxRegistered ? [0, malaysiaRateByActivity(invoice.businessActivity)] : [0],
      rateNote: text(
        "For Malaysia e-Invoice, supplier and buyer identity fields, line classification codes, totals, and issue time are mandatory. Turnover below RM1 million is not a blanket safe harbour, so use this path whenever the transaction still falls into the rollout scope.",
        "马来西亚 e-Invoice 要求卖方与买方身份字段、行分类编码、金额汇总和开票时间必须完整。低于 RM1 million 并不等于天然豁免，只要交易仍落入实施范围，就应走这一路径。",
        "Untuk e-Invois Malaysia, medan identiti pembekal dan pembeli, kod klasifikasi item, jumlah dan masa terbitan adalah mandatori. Perolehan di bawah RM1 juta bukan jaminan pengecualian mutlak, jadi gunakan laluan ini apabila transaksi masih termasuk dalam skop pelaksanaan.",
        "Untuk e-Invoice Malaysia, identitas pemasok dan pembeli, kode klasifikasi item, total, dan waktu terbit wajib diisi. Omzet di bawah RM1 juta bukan jaminan pengecualian mutlak, jadi gunakan jalur ini bila transaksi masih masuk ruang lingkup implementasi."
      ),
      sourceIds: ["MY_MYINVOIS_INVOICE_V11", "MY_HASIL_EINVOICE_TIMELINE", "MY_HASIL_EINVOICE_FAQ", "MY_MYINVOIS_VALIDATE_TIN"]
    };
  }

  return {
    key: "profile_my_sst",
    label: text("Malaysia SST invoice", "马来西亚 SST 发票", "Invois SST Malaysia", "Invoice SST Malaysia"),
    requiredFields: [
      "invoiceNumber",
      "issueDate",
      "totals.currency",
      "seller.legalName",
      "seller.registrationNumber",
      "seller.taxNumber",
      "seller.address"
    ],
    requiredLineFields: ["description", "quantity", "unitPrice", "amountExcludingTax", "taxRate", "taxAmount", "totalAmount"],
    allowedTaxRates: [0, malaysiaRateByActivity(invoice.businessActivity)],
    rateNote: text(
      "Registered SST service providers must issue invoices with prescribed particulars and show tax separately.",
      "已注册 SST 的服务提供者应开具包含法定要素、且税额单独列示的发票。",
      "Penyedia perkhidmatan SST berdaftar mesti mengeluarkan invois dengan butiran yang ditetapkan dan menunjukkan cukai secara berasingan.",
      "Penyedia jasa SST terdaftar harus menerbitkan invoice dengan rincian yang diwajibkan dan menampilkan pajak secara terpisah."
    ),
    sourceIds: ["MY_MYSST_TAX_INVOICES", "MY_MYSST_RETURN_PAYMENT_GUIDE", "MY_MYSST_BACKGROUND"]
  };
};

const idProfile = (invoice: PreparedInvoice): CountryProfile => {
  if (!invoice.taxRegistered) {
    return {
      key: "profile_id_non_registered",
      label: text("Indonesia commercial invoice", "印度尼西亚普通商业发票", "Invois komersial Indonesia", "Invoice komersial Indonesia"),
      requiredFields: ["invoiceNumber", "issueDate", "totals.currency", "seller.legalName", "seller.registrationNumber", "seller.address"],
      requiredLineFields: ["description", "quantity", "unitPrice", "amountExcludingTax", "totalAmount"],
      allowedTaxRates: [0],
      rateNote: text("Businesses that are not PKP should not charge VAT.", "未成为 PKP 的主体不应计收增值税。", "Perniagaan yang belum PKP tidak boleh mengenakan VAT.", "Usaha yang belum PKP tidak boleh memungut PPN."),
      sourceIds: ["ID_DJP_PPN_COLLECTION"]
    };
  }

  if (invoice.buyerType === "final_consumer") {
    return {
      key: "profile_id_retail",
      label: text("Indonesia retail tax invoice", "印度尼西亚零售税务发票", "Faktur cukai runcit Indonesia", "Faktur pajak ritel Indonesia"),
      requiredFields: [
        "invoiceNumber",
        "issueDate",
        "seller.legalName",
        "seller.address",
        "seller.taxNumber",
        "countryExtensions.transactionCode"
      ],
      requiredLineFields: ["description", "amountExcludingTax", "taxAmount", "totalAmount"],
      allowedTaxRates: invoice.supplyNature === "domestic" ? [indonesiaRateByActivity(invoice.businessActivity)] : [0, indonesiaRateByActivity(invoice.businessActivity)],
      rateNote: text(
        "Retail PKP invoices may omit buyer identity, but must still include seller details, goods/services, tax, and code/serial/date. Use 11% for ordinary domestic supplies and reserve 12% for the luxury path.",
        "零售 PKP 发票可以省略买方身份信息，但仍必须包含卖方信息、货物/服务、税额以及代码/流水号/日期。普通境内交易通常按 11% 路径，12% 仅用于奢侈品路径。",
        "Faktur PKP runcit boleh mengecualikan identiti pembeli tetapi masih mesti menunjukkan butiran penjual, barang/perkhidmatan, cukai dan kod/nombor siri/tarikh. Gunakan 11% untuk bekalan domestik biasa dan 12% hanya untuk laluan barang mewah.",
        "Faktur PKP ritel boleh tanpa identitas pembeli, tetapi tetap wajib memuat data penjual, barang/jasa, pajak, serta kode/nomor seri/tanggal. Gunakan 11% untuk penyerahan domestik biasa dan 12% hanya untuk jalur barang mewah."
      ),
      sourceIds: ["ID_DJP_FAKTUR_REGULATION", "ID_DJP_PPN_RATE"]
    };
  }

  return {
    key: "profile_id_pkp",
    label: text("Indonesia PKP e-Faktur draft", "印度尼西亚 PKP e-Faktur 草稿", "Draf e-Faktur PKP Indonesia", "Draf e-Faktur PKP Indonesia"),
    requiredFields: [
      "invoiceNumber",
      "issueDate",
      "seller.legalName",
      "seller.address",
      "seller.taxNumber",
      "buyer.legalName",
      "buyer.address",
      "buyer.taxNumber",
      "countryExtensions.transactionCode"
    ],
    requiredLineFields: ["description", "quantity", "unitPrice", "amountExcludingTax", "taxRate", "taxAmount", "totalAmount"],
    allowedTaxRates: invoice.supplyNature === "domestic" ? [indonesiaRateByActivity(invoice.businessActivity)] : [0, indonesiaRateByActivity(invoice.businessActivity)],
    rateNote: text(
      "PKP invoices must be electronic and include seller and buyer identity, goods/services, tax, code, serial number, and date. In normal domestic cases the effective VAT path remains 11%, while 12% is reserved for the luxury path from 1 January 2025.",
      "PKP 发票必须以电子形式开具，并包含买卖双方身份信息、货物/服务、税额、代码、流水号和日期。普通境内交易的有效 VAT 路径仍是 11%，自 2025-01-01 起 12% 仅用于奢侈品路径。",
      "Faktur PKP mesti berbentuk elektronik dan mengandungi identiti penjual/pembeli, barang/perkhidmatan, cukai, kod, nombor siri dan tarikh. Dalam kes domestik biasa laluan VAT efektif kekal 11%, manakala 12% dikhaskan untuk laluan barang mewah mulai 1 Januari 2025.",
      "Faktur PKP wajib berbentuk elektronik dan memuat identitas penjual/pembeli, barang/jasa, pajak, kode, nomor seri, dan tanggal. Pada transaksi domestik biasa jalur VAT efektif tetap 11%, sedangkan 12% dikhususkan untuk jalur barang mewah mulai 1 Januari 2025."
    ),
    sourceIds: ["ID_DJP_FAKTUR_REGULATION", "ID_DJP_PPN_RATE", "ID_DJP_FAKTUR_CHANNELS"]
  };
};

const thProfile = (invoice: PreparedInvoice): CountryProfile => {
  const mode = thailandInvoiceMode(invoice);
  const crossBorderAllowed = invoice.supplyNature === "domestic" ? [7] : [0, 7];

  if (mode === "commercial_invoice" || !invoice.taxRegistered) {
    return {
      key: "profile_th_non_registered",
      label: text("Thailand commercial invoice", "泰国普通商业发票", "Invois komersial Thailand", "Invoice komersial Thailand", "ใบแจ้งหนี้เชิงพาณิชย์ไทย"),
      requiredFields: ["invoiceNumber", "issueDate", "totals.currency", "seller.legalName", "seller.address"],
      requiredLineFields: ["description", "quantity", "unitPrice", "amountExcludingTax", "totalAmount"],
      allowedTaxRates: [0],
      rateNote: text(
        "Businesses not registered for Thai VAT should not charge VAT on the invoice.",
        "未完成泰国 VAT 注册的经营者不应在发票上计收 VAT。",
        "Perniagaan yang belum berdaftar VAT Thailand tidak boleh mengenakan VAT pada invois.",
        "Usaha yang belum terdaftar VAT Thailand tidak boleh memungut VAT pada invoice.",
        "ผู้ประกอบการที่ยังไม่จดทะเบียน VAT ไทยไม่ควรเรียกเก็บ VAT ในเอกสาร"
      ),
      sourceIds: ["TH_RD_VAT_OVERVIEW"]
    };
  }

  if (mode === "etax_invoice") {
    return {
      key: "profile_th_etax",
      label: text("Thailand e-Tax invoice / e-Receipt draft", "泰国电子税务发票 / 电子收据草稿", "Draf e-Tax invoice / e-Receipt Thailand", "Draf e-Tax invoice / e-Receipt Thailand", "ร่าง e-Tax Invoice / e-Receipt ไทย"),
      requiredFields: ["invoiceNumber", "issueDate", "totals.currency", "seller.legalName", "seller.taxNumber", "seller.address", "buyer.legalName", "buyer.address"],
      requiredLineFields: ["description", "quantity", "unitPrice", "amountExcludingTax", "taxAmount", "totalAmount"],
      allowedTaxRates: crossBorderAllowed,
      rateNote: text(
        "Thai e-Tax issuance requires use of the Revenue Department electronic route and supporting digital-signature or timestamp controls.",
        "泰国电子税务发票需要通过税务局认可的电子路径出具，并满足数字签名或时间戳等控制要求。",
        "e-Tax Thailand memerlukan laluan elektronik yang diiktiraf oleh Revenue Department serta kawalan tandatangan digital atau cap masa.",
        "e-Tax Thailand memerlukan jalur elektronik yang diakui Revenue Department serta kontrol tanda tangan digital atau cap waktu.",
        "การออก e-Tax ของไทยต้องใช้ช่องทางอิเล็กทรอนิกส์ที่กรมสรรพากรกำหนด พร้อมมาตรการลายมือชื่อดิจิทัลหรือประทับรับรองเวลา"
      ),
      sourceIds: ["TH_RD_TAX_INVOICE_SECTIONS", "TH_RD_ETAX_OVERVIEW"]
    };
  }

  if (mode === "abbreviated_tax_invoice") {
    return {
      key: "profile_th_abbreviated",
      label: text("Thailand abbreviated tax invoice", "泰国简式税务发票", "Invois cukai ringkas Thailand", "Invoice pajak ringkas Thailand", "ใบกำกับภาษีอย่างย่อไทย"),
      requiredFields: ["invoiceNumber", "issueDate", "seller.legalName", "seller.taxNumber", "buyer.legalName", "buyer.address"],
      requiredLineFields: ["description", "quantity", "totalAmount"],
      allowedTaxRates: [7],
      rateNote: text(
        "Abbreviated Thai tax invoices are aimed at retail flows; the shown price should clearly be VAT-inclusive.",
        "泰国简式税务发票主要面向零售场景，显示价格应明确为已含 VAT。",
        "Invois cukai ringkas Thailand terutamanya untuk aliran runcit; harga yang dipaparkan hendaklah jelas termasuk VAT.",
        "Invoice pajak ringkas Thailand terutama ditujukan untuk alur ritel; harga yang ditampilkan harus jelas sudah termasuk VAT.",
        "ใบกำกับภาษีอย่างย่อของไทยเหมาะกับธุรกิจค้าปลีก และราคาที่แสดงควรระบุชัดว่าเป็นราคารวม VAT"
      ),
      sourceIds: ["TH_RD_TAX_INVOICE_SECTIONS"]
    };
  }

  return {
    key: "profile_th_vat",
    label: text("Thailand VAT tax invoice", "泰国 VAT 税务发票", "Invois cukai VAT Thailand", "Invoice pajak VAT Thailand", "ใบกำกับภาษี VAT ไทย"),
    requiredFields: ["invoiceNumber", "issueDate", "totals.currency", "seller.legalName", "seller.taxNumber", "seller.address", "buyer.legalName", "buyer.address"],
    requiredLineFields: ["description", "quantity", "unitPrice", "amountExcludingTax", "taxAmount", "totalAmount"],
    allowedTaxRates: crossBorderAllowed,
    rateNote: text(
      "Thai tax invoices should normally be in Thai language and THB; foreign language or currency needs approval.",
      "泰国税务发票通常应使用泰语和 THB；若使用外语或外币，需要主管机关批准。",
      "Invois cukai Thailand lazimnya perlu dalam bahasa Thai dan THB; bahasa atau mata wang asing memerlukan kelulusan.",
      "Invoice pajak Thailand pada umumnya harus berbahasa Thai dan menggunakan THB; bahasa atau mata uang asing memerlukan persetujuan.",
      "ใบกำกับภาษีไทยโดยทั่วไปต้องเป็นภาษาไทยและสกุลเงินบาท หากใช้นอกเหนือจากนี้ต้องได้รับอนุมัติ"
    ),
    sourceIds: ["TH_RD_TAX_INVOICE_SECTIONS", "TH_RD_VAT_OVERVIEW", "TH_RD_VAT_RATE_2026"]
  };
};

const sgChecks = (invoice: PreparedInvoice): CountrySpecificCheckResult[] => {
  const checks: CountrySpecificCheckResult[] = [];
  if (invoice.seller.registrationNumber && !validateSingaporeRegistration(invoice.seller.registrationNumber)) {
    checks.push({
      type: "format",
      severity: "warning",
      riskLevel: "medium",
      fieldPath: "seller.registrationNumber",
      sourceIds: ["SG_IRAS_UEN_FORMAT"],
      messageKey: "format"
    });
  }
  if (invoice.taxRegistered && invoice.buyerType === "final_consumer" && invoice.totals.grandTotal <= 1_000 && !includesTaxNote(invoice)) {
    checks.push({
      type: "required_field",
      severity: "warning",
      riskLevel: "low",
      fieldPath: "notes",
      sourceIds: ["SG_IRAS_INVOICING_CUSTOMERS"],
      messageKey: "simplified-price-note"
    });
  }
  if (invoice.taxRegistered && invoice.supplyNature === "export_services" && !invoice.supportingEvidence?.overseasCustomerEvidence) {
    checks.push({
      type: "cross_border",
      severity: "warning",
      riskLevel: "medium",
      sourceIds: ["SG_IRAS_INTL_SERVICES"],
      messageKey: "cross-border-evidence"
    });
  }
  return checks;
};

const myChecks = (invoice: PreparedInvoice): CountrySpecificCheckResult[] => {
  const checks: CountrySpecificCheckResult[] = [];
  if (invoice.taxRegistered && invoice.seller.taxNumber && !validateMalaysiaTin(invoice.seller.taxNumber)) {
    checks.push({
      type: "format",
      severity: "warning",
      riskLevel: "medium",
      fieldPath: "seller.taxNumber",
      sourceIds: ["MY_MYINVOIS_VALIDATE_TIN"],
      messageKey: "format"
    });
  }
  if ((invoice.eInvoiceRequested || (invoice.turnoverLast12Months ?? 0) >= 1_000_000) && !invoice.issueTime) {
    checks.push({
      type: "required_field",
      severity: "error",
      riskLevel: "high",
      fieldPath: "issueTime",
      sourceIds: ["MY_MYINVOIS_INVOICE_V11"],
      messageKey: "my-issue-time"
    });
  }
  if (invoice.taxRegistered && invoice.businessActivity === "it_services" && invoice.supplyNature === "export_services") {
    checks.push({
      type: "note",
      severity: "info",
      riskLevel: "low",
      sourceIds: ["MY_MYSST_IT_GUIDE"],
      messageKey: "my-it-outside-malaysia"
    });
  }
  return checks;
};

const idChecks = (invoice: PreparedInvoice): CountrySpecificCheckResult[] => {
  const checks: CountrySpecificCheckResult[] = [];
  if (invoice.taxRegistered && invoice.seller.taxNumber && !validateIndonesiaTaxNumber(invoice.seller.taxNumber)) {
    checks.push({
      type: "format",
      severity: "warning",
      riskLevel: "medium",
      fieldPath: "seller.taxNumber",
      sourceIds: ["ID_DJP_NPWP_16"],
      messageKey: "format"
    });
  }
  if (invoice.taxRegistered && invoice.buyerType !== "final_consumer" && invoice.buyer.taxNumber && !validateIndonesiaTaxNumber(invoice.buyer.taxNumber)) {
    checks.push({
      type: "format",
      severity: "warning",
      riskLevel: "medium",
      fieldPath: "buyer.taxNumber",
      sourceIds: ["ID_DJP_NPWP_16"],
      messageKey: "format"
    });
  }
  if (invoice.taxRegistered && invoice.supplyNature === "export_services" && !invoice.supportingEvidence?.writtenContract) {
    checks.push({
      type: "cross_border",
      severity: "warning",
      riskLevel: "medium",
      sourceIds: ["ID_DJP_EXPORT_SERVICES"],
      messageKey: "cross-border-evidence"
    });
  }
  return checks;
};

const thChecks = (invoice: PreparedInvoice): CountrySpecificCheckResult[] => {
  const checks: CountrySpecificCheckResult[] = [];
  const mode = thailandInvoiceMode(invoice);

  if (invoice.taxRegistered && invoice.seller.taxNumber && !validateThailandTaxNumber(invoice.seller.taxNumber)) {
    checks.push({
      type: "format",
      severity: "warning",
      riskLevel: "medium",
      fieldPath: "seller.taxNumber",
      sourceIds: ["TH_RD_TIN_SERVICE"],
      messageKey: "format"
    });
  }

  if (invoice.taxRegistered && invoice.buyer.taxNumber && !validateThailandTaxNumber(invoice.buyer.taxNumber)) {
    checks.push({
      type: "format",
      severity: "warning",
      riskLevel: "medium",
      fieldPath: "buyer.taxNumber",
      sourceIds: ["TH_RD_TIN_SERVICE"],
      messageKey: "format"
    });
  }

  if (mode === "etax_invoice" && !invoice.countryExtensions?.etaxRegistered) {
    checks.push({
      type: "required_field",
      severity: "warning",
      riskLevel: "medium",
      fieldPath: "countryExtensions.etaxRegistered",
      sourceIds: ["TH_RD_ETAX_OVERVIEW"],
      messageKey: "th-etax-approval"
    });
  }

  if (invoice.taxRegistered && invoice.totals.currency !== "THB" && !invoice.countryExtensions?.foreignCurrencyApproval) {
    checks.push({
      type: "note",
      severity: "warning",
      riskLevel: "medium",
      fieldPath: "countryExtensions.foreignCurrencyApproval",
      sourceIds: ["TH_RD_TAX_INVOICE_SECTIONS"],
      messageKey: "th-foreign-approval"
    });
  }

  if (mode === "abbreviated_tax_invoice" && invoice.buyerType !== "final_consumer") {
    checks.push({
      type: "note",
      severity: "info",
      riskLevel: "low",
      fieldPath: "invoiceType",
      sourceIds: ["TH_RD_TAX_INVOICE_SECTIONS"],
      messageKey: "th-abbreviated-retail"
    });
  }

  if (invoice.taxRegistered && invoice.supplyNature === "export_services" && !invoice.supportingEvidence?.writtenContract) {
    checks.push({
      type: "cross_border",
      severity: "warning",
      riskLevel: "medium",
      sourceIds: ["TH_RD_VAT_OVERVIEW"],
      messageKey: "cross-border-evidence"
    });
  }

  return checks;
};

export const countryPacks: Record<string, CountryPack> = {
  SG: {
    code: "SG",
    name: text("Singapore", "新加坡", "Singapura", "Singapura"),
    localCurrency: "SGD",
    defaultLocale: "en",
    supportedLocales: ["en", "zh-CN", "ms-MY", "id-ID", "th-TH"],
    rulebookVersion: "2026.04.09-sg",
    thresholdRules: sgThresholdRules,
    resolveProfile: sgProfile,
    validateRegistrationNumber: validateSingaporeRegistration,
    extraChecks: sgChecks,
    sourceIds: [
      "SG_IRAS_INVOICING_CUSTOMERS",
      "SG_IRAS_GST_REGISTRATION",
      "SG_IRAS_GST_RESPONSIBILITIES",
      "SG_IRAS_GST_INVOICENOW_FAQ",
      "SG_IRAS_INTL_SERVICES",
      "SG_IRAS_UEN_FORMAT"
    ]
  },
  MY: {
    code: "MY",
    name: text("Malaysia", "马来西亚", "Malaysia", "Malaysia"),
    localCurrency: "MYR",
    defaultLocale: "ms-MY",
    supportedLocales: ["en", "zh-CN", "ms-MY", "id-ID", "th-TH"],
    rulebookVersion: "2026.04.09-my",
    thresholdRules: myThresholdRules,
    resolveProfile: myProfile,
    validateTaxNumber: validateMalaysiaTin,
    extraChecks: myChecks,
    sourceIds: [
      "MY_HASIL_EINVOICE_TIMELINE",
      "MY_HASIL_EINVOICE_FAQ",
      "MY_MYINVOIS_INVOICE_V11",
      "MY_MYINVOIS_VALIDATE_TIN",
      "MY_MYSST_BACKGROUND",
      "MY_MYSST_TAX_INVOICES",
      "MY_MYSST_RETURN_PAYMENT_GUIDE",
      "MY_MYSST_REGISTRATION_TABLE",
      "MY_MYSST_IT_GUIDE"
    ]
  },
  ID: {
    code: "ID",
    name: text("Indonesia", "印度尼西亚", "Indonesia", "Indonesia"),
    localCurrency: "IDR",
    defaultLocale: "id-ID",
    supportedLocales: ["en", "zh-CN", "ms-MY", "id-ID", "th-TH"],
    rulebookVersion: "2026.04.09-id",
    thresholdRules: idThresholdRules,
    resolveProfile: idProfile,
    validateTaxNumber: validateIndonesiaTaxNumber,
    extraChecks: idChecks,
    sourceIds: [
      "ID_DJP_PPN_COLLECTION",
      "ID_DJP_FAKTUR_REGULATION",
      "ID_DJP_PPN_RATE",
      "ID_DJP_EXPORT_SERVICES",
      "ID_DJP_NPWP_16",
      "ID_DJP_FAKTUR_CHANNELS"
    ]
  },
  TH: {
    code: "TH",
    name: text("Thailand", "泰国", "Thailand", "Thailand", "ประเทศไทย"),
    localCurrency: "THB",
    defaultLocale: "th-TH",
    supportedLocales: ["en", "zh-CN", "ms-MY", "id-ID", "th-TH"],
    rulebookVersion: "2026.04.09-th",
    thresholdRules: thThresholdRules,
    resolveProfile: thProfile,
    validateTaxNumber: validateThailandTaxNumber,
    extraChecks: thChecks,
    sourceIds: [
      "TH_RD_VAT_OVERVIEW",
      "TH_RD_TAX_INVOICE_SECTIONS",
      "TH_RD_VAT_RATE_2026",
      "TH_RD_TIN_SERVICE",
      "TH_RD_ETAX_OVERVIEW"
    ]
  }
};

export const getCountryPack = (country: string): CountryPack => countryPacks[country];

